import * as React from 'react';
import { Helper } from '../../../Helper';
import { Environment } from '../../../services/Environment';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import { IAButton } from '../../button/Button';
import { IAIcon } from '../../icon/Icon';
import * as styles from "./MediaSelectorPreview.css";
import { LocalizationService } from '../../../services/LocalizationService';
import { IASkeleton } from '../../skeleton/Skeleton';
import { ICropSetting } from '../../../interfaces/ICropSetting';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';

export interface IAMediaSelectorPreviewProps {
  token: string;
  tenant: string;
  environment: IEnvironment;
  fileType: "image" | "video";
  highlightColor: string;
  url: string;
  style?: React.CSSProperties;
  imageCrop?: ICropSetting;
  displayMode?: "default" | "list" | "grid";
  onClearImage: () => void;
}

export interface IAMediaSelectorPreviewState {
  isLoading: boolean;
  hasError: boolean;
}

export class IAMediaSelectorPreview extends React.Component<IAMediaSelectorPreviewProps, IAMediaSelectorPreviewState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: IAMediaSelectorPreviewProps) {
    super(props);
    this.state = {
      isLoading: true,
      hasError: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public render(): JSX.Element {
    const isListMode = this.props.displayMode === "list";
    const isGridMode = this.props.displayMode === "grid";
    const GRID_TILE_SIZE = 40;
    const style = this.props.style ?? {};
    const isBlob = this.props.url.startsWith("blob:");
    return (
      <div
        className={styles.IA_MediaSelectorPreviewContainer}
        style={
          isListMode
            ? { ...style, height: 50, width: "100%", justifyContent: "flex-start", marginBottom: 5, marginTop: 5 }
            : isGridMode
              ? { ...style, height: GRID_TILE_SIZE, width: GRID_TILE_SIZE }
              : style
        }
      >
        {this.props.fileType === "image"
          ?
          <img
            className={styles.IA_MediaSelectorPreviewImage}
            alt=""
            style={{
              opacity: this.state.isLoading ? 0 : 1,
              display: this.state.hasError ? "none" : undefined,
              position: this.state.isLoading ? "absolute" : undefined,
              ...(isListMode ? { width: 50, height: 50, flex: "0 0 auto", objectFit: "cover" } : {}),
              ...(isGridMode ? { width: GRID_TILE_SIZE, height: GRID_TILE_SIZE, objectFit: "cover" } : {})
            }}
            src={isBlob ? this.props.url : Helper.getImageFromApiWhenOutsideSharepoint(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, this.props.url, "", "Full", this.props.imageCrop, true, !!!this.props.imageCrop)}
            onLoad={() => this.setState({ isLoading: false, hasError: false })}
            onError={() => this.setState({ isLoading: false, hasError: true })}
          />
          :
          <video
            style={{
              opacity: this.state.isLoading ? 0 : 1,
              display: this.state.hasError ? "none" : undefined,
              position: this.state.isLoading ? "absolute" : undefined,
              borderRadius: 5,
              width: isListMode ? 50 : isGridMode ? GRID_TILE_SIZE : "100%",
              height: isListMode ? 50 : isGridMode ? GRID_TILE_SIZE : undefined,
              flex: isListMode ? "0 0 auto" : undefined,
              objectFit: isListMode || isGridMode ? "cover" : undefined
            }}
            src={isBlob ? this.props.url : Helper.getVideoUrl(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, this.props.url)}
            controls
            disablePictureInPicture
            controlsList="nodownload noplaybackrate"
            preload="metadata"
            onLoadedData={() => this.setState({ isLoading: false, hasError: false })}
            onError={() => this.setState({ isLoading: false, hasError: true })}
          />
        }
        {this.state.isLoading &&
          <div
            style={
              isListMode
                ? { width: 50, height: 50, flex: "0 0 auto" }
                : isGridMode
                  ? { width: GRID_TILE_SIZE, height: GRID_TILE_SIZE }
                  : { width: "100%", aspectRatio: "16 / 9" }
            }
          >
            <IASkeleton
              skeleton={{
                height: "100%",
                width: "100%",
                borderRadius: 5
              }}
            />
          </div>
        }
        {this.state.hasError && <p className={styles.IA_MediaSelectorPreviewErrorText}>{this.localizationService.strings.MediaSelector_ErrorLoadingPreview}</p>}
        {!this.state.isLoading && this.props.onClearImage && !isGridMode &&
          <IAButton
            label="Clear image"
            icon="Trash"
            style={
              isListMode
                ? { marginLeft: "auto", padding: "0 10px" }
                : { position: "absolute", top: 0, right: 0, padding: "0 10px" }
            }
            buttonColor="rgba(0,0,0,.3)"
            mouseOverButtonColor="rgba(0,0,0,.2)"
            borderRadius={5}
            onClick={() => this.props.onClearImage()}
          />
        }
        {!this.state.isLoading && this.props.onClearImage && isGridMode &&
          // Plain flex-centered div instead of IAButton - IAButton's icon-only layout
          // (float + asymmetric padding tuned for larger labeled buttons) left the icon
          // visibly off-center inside a small circular overlay like this one.
          <div
            className="IA_OnFocus"
            role="button"
            tabIndex={0}
            aria-label="Remove image"
            onClick={() => this.props.onClearImage()}
            onKeyDown={(event) => {
              if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                event.preventDefault();
                this.props.onClearImage();
              }
            }}
            style={{
              position: "absolute",
              top: -8,
              right: -8,
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,.5)",
              cursor: "pointer"
            }}
          >
            <IAIcon title="Close" size={12} color="#ffffff" />
          </div>
        }
      </div>
    );
  }
}
